/**
 * 家长端 · 兑换审批（见契约 1.3「兑换审批」）。
 *
 * 待处理列表：同意 / 拒绝 / 标记已发放。
 * 每条都显示**孩子当前余额**与**这次兑换后会剩多少** —— 家长审批时最需要的就是这个数。
 *
 * 流程语义（方案 3.4.2）：
 *  - `need_approval = false`：孩子点了就 `approved` 并立即扣分，等着家长标记 `fulfilled`；
 *  - `need_approval = true` ：先 `pending`；家长同意 → `approved`（此时才扣分）→ `fulfilled`；
 *    拒绝 → `rejected`（不扣分）。
 *
 * 幂等：所有审批动作都带 `Idempotency-Key`，家长手抖连点两次不会重复扣分/发货。
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { REDEMPTION_STATUSES, type RedemptionStatus } from '@fht/shared';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { ErrorState, NotReadyState, StatTile } from '../../components/ui/Bits';
import {
  usePointsSummary,
  useRedemptionMutations,
  useRedemptions,
} from '../../lib/domainQueries';
import { useStudent } from '../../lib/queries';
import { useParentActor } from '../../lib/ActorContext';
import { canDecideRedeem } from '../../lib/uiMode';
import { newIdempotencyKey } from '../../lib/api/client';
import { toErrorMessage } from '../../lib/api/error';
import { cn, formatDateTimeLocal } from '../../lib/utils';
import type { Redemption } from '../../lib/api/types';

const STATUS_META: Record<
  RedemptionStatus,
  { label: string; tone: 'warn' | 'success' | 'danger' | 'neutral'; icon: string }
> = {
  pending: { label: '待审批', tone: 'warn', icon: '⏳' },
  approved: { label: '已批准 · 待发放', tone: 'success', icon: '✅' },
  fulfilled: { label: '已发放', tone: 'success', icon: '🎉' },
  rejected: { label: '已拒绝', tone: 'danger', icon: '🚫' },
  cancelled: { label: '已取消', tone: 'neutral', icon: '↩️' },
};

const FILTER_LABEL: Record<'all' | RedemptionStatus, string> = {
  all: '全部',
  pending: '待审批',
  approved: '待发放',
  fulfilled: '已发放',
  rejected: '已拒绝',
  cancelled: '已取消',
};

interface DecisionState {
  redemption: Redemption;
  decision: 'approve' | 'reject' | 'fulfill';
}

export function ParentRedemptionsPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const studentQuery = useStudent(id);
  const actor = useParentActor(studentQuery.data?.permission ?? null);
  const canDecide = canDecideRedeem(actor);

  const [filter, setFilter] = useState<'all' | RedemptionStatus>('all');
  const listQuery = useRedemptions(id, filter === 'all' ? undefined : filter);
  const pointsQuery = usePointsSummary(id);
  const mutations = useRedemptionMutations(id);

  const [decision, setDecision] = useState<DecisionState | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** 一次弹窗打开 = 一个幂等键；重试复用，避免重复扣分。 */
  const [idempotencyKey, setIdempotencyKey] = useState('');

  const redemptions = listQuery.data?.redemptions ?? [];
  const notReady = listQuery.data?.notReady ?? false;
  const balance = pointsQuery.data?.balance ?? 0;

  const pendingList = useMemo(
    () => redemptions.filter((item) => item.status === 'pending'),
    [redemptions],
  );
  const approvedList = useMemo(
    () => redemptions.filter((item) => item.status === 'approved'),
    [redemptions],
  );

  const openDecision = (redemption: Redemption, next: DecisionState['decision']) => {
    setDecision({ redemption, decision: next });
    setNote('');
    setError(null);
    setIdempotencyKey(newIdempotencyKey(`redemption:${redemption.id}:${next}`));
  };

  const submit = async () => {
    if (!decision) return;
    setError(null);
    try {
      await mutations.decide.mutateAsync({
        id: decision.redemption.id,
        decision: decision.decision,
        note: note.trim(),
        idempotencyKey,
      });
      toast.showToast(
        decision.decision === 'approve'
          ? '已同意，积分已按规则扣除'
          : decision.decision === 'reject'
            ? '已拒绝，不扣积分'
            : '已标记为发放',
        'success',
      );
      setDecision(null);
    } catch (err) {
      setError(toErrorMessage(err));
    }
  };

  /**
   * 这次兑换之后余额会变成多少。
   * 只有"把 pending 改成 approved"才真正扣分；拒绝与标记发放都不动积分
   * （见方案 3.4.2：need_approval=false 的奖品在申请时就已扣分）。
   */
  const balanceAfter = (redemption: Redemption, next: DecisionState['decision']): number | null => {
    if (next !== 'approve') return null;
    if (redemption.status !== 'pending') return null;
    return balance - redemption.costPoints;
  };

  return (
    <AppShell
      kind="parent"
      title={studentQuery.data ? `${studentQuery.data.nickname} · 兑换审批` : '兑换审批'}
      headerAction={
        <Link to={`/students/${id}/rewards`}>
          <Button size="sm" variant="secondary">
            奖品目录
          </Button>
        </Link>
      }
    >
      <div className="space-y-6">
        {/* ---------- 概览：余额是审批的核心信息 ---------- */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile
            label="孩子当前余额"
            value={pointsQuery.data?.notReady ? '—' : balance}
            tone="amber"
            hint={pointsQuery.data?.notReady ? '积分接口尚未就绪' : undefined}
          />
          <StatTile label="待审批" value={pendingList.length} tone="brand" />
          <StatTile label="待发放" value={approvedList.length} tone="emerald" />
          <StatTile label="记录条数" value={redemptions.length} tone="slate" />
        </div>

        {balance < 0 && (
          <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            ⚠️ 孩子当前积分是负数（透支）。这通常是因为先兑换、之后家长做了冲正 ——
            账本依然自洽（余额 = 所有流水的合计），下次打卡会先补回来。
          </p>
        )}

        {/* ---------- 筛选 ---------- */}
        <div className="flex flex-wrap gap-2">
          {(['all', ...REDEMPTION_STATUSES] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              aria-pressed={filter === value}
              className={cn(
                'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
                filter === value ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100',
              )}
            >
              {FILTER_LABEL[value]}
            </button>
          ))}
        </div>

        {/* ---------- 列表 ---------- */}
        {listQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : listQuery.isError ? (
          <ErrorState message={toErrorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        ) : notReady ? (
          <NotReadyState feature="兑换审批" />
        ) : redemptions.length === 0 ? (
          <EmptyState
            icon="🎁"
            title="还没有兑换记录"
            description={
              filter === 'all'
                ? '孩子在奖品屋点「兑换」之后，记录会出现在这里，由你同意或拒绝。'
                : '这个状态下暂时没有记录。'
            }
          />
        ) : (
          <ul className="space-y-3">
            {redemptions.map((redemption) => {
              const meta = STATUS_META[redemption.status];
              const after = balance - redemption.costPoints;
              return (
                <li key={redemption.id}>
                  <Card>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span aria-hidden="true">{meta.icon}</span>
                          <h3 className="font-semibold text-slate-900">
                            {redemption.rewardNameSnapshot}
                          </h3>
                          <Badge tone={meta.tone}>{meta.label}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-slate-500">
                          花费 {redemption.costPoints} 分 · 申请于 {formatDateTimeLocal(redemption.requestedAt)}
                        </p>
                        {redemption.note && (
                          <p className="mt-1 text-xs text-slate-500">孩子留言：{redemption.note}</p>
                        )}
                        {redemption.decisionNote && (
                          <p className="mt-1 text-xs text-slate-500">你的说明：{redemption.decisionNote}</p>
                        )}
                        {/* 审批最关键的一行：换完还剩多少 */}
                        <p
                          className={cn(
                            'mt-2 text-sm font-medium',
                            after < 0 ? 'text-rose-600' : 'text-slate-600',
                          )}
                        >
                          孩子现在 {balance} 分，这次兑换后
                          {redemption.status === 'pending' ? (
                            after < 0 ? (
                              <span className="text-rose-600">会变成 {after} 分（积分透支）</span>
                            ) : (
                              <span className="text-emerald-600">还剩 {after} 分</span>
                            )
                          ) : (
                            <span className="text-slate-500">的记录已确定</span>
                          )}
                        </p>
                      </div>

                      {canDecide && (redemption.status === 'pending' || redemption.status === 'approved') && (
                        <div className="flex shrink-0 flex-wrap gap-2">
                          {redemption.status === 'pending' && (
                            <>
                              <Button size="sm" onClick={() => openDecision(redemption, 'approve')}>
                                同意
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => openDecision(redemption, 'reject')}
                              >
                                拒绝
                              </Button>
                            </>
                          )}
                          {redemption.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="kid-green"
                              onClick={() => openDecision(redemption, 'fulfill')}
                            >
                              标记已发放
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}

        <Card>
          <CardHeader title="兑换规则（与孩子看到的完全一致）" />
          <ul className="list-inside list-disc space-y-1 text-sm text-slate-500">
            <li>「需审批」的奖品：你同意时才扣分；拒绝不扣分，孩子会看到「积分退回」。</li>
            <li>「不需审批」的奖品：孩子一点就扣分，等你发放实物后点「标记已发放」。</li>
            <li>扣分与余额校验在服务端同一个事务里完成，连点两次也不会超兑。</li>
            <li>所有审批动作都带幂等键，网络重试不会重复扣分。</li>
          </ul>
        </Card>
      </div>

      {/* ---------- 审批确认 ---------- */}
      <Modal
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={
          decision?.decision === 'approve'
            ? '同意这次兑换？'
            : decision?.decision === 'reject'
              ? '拒绝这次兑换？'
              : '标记为已发放？'
        }
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => setDecision(null)}>
              取消
            </Button>
            <Button
              variant={decision?.decision === 'reject' ? 'danger' : 'primary'}
              loading={mutations.decide.isPending}
              onClick={() => void submit()}
            >
              确认
            </Button>
          </div>
        }
      >
        {decision && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              「{decision.redemption.rewardNameSnapshot}」需要 {decision.redemption.costPoints} 分。
            </p>

            {decision.decision === 'reject' ? (
              <p className="text-sm text-slate-600">
                拒绝<strong>不会</strong>扣分，孩子会看到「积分退回」的说明。
              </p>
            ) : decision.decision === 'fulfill' ? (
              <p className="text-sm text-slate-600">
                标记已发放只是记录状态，不会再动积分（积分在批准时已经扣过了）。
              </p>
            ) : (
              (() => {
                const after = balanceAfter(decision.redemption, decision.decision);
                return (
                  <div className="rounded-xl bg-slate-50 p-3 text-sm">
                    <p className="text-slate-600">
                      现在余额 <span className="font-semibold">{balance}</span> 分
                    </p>
                    <p className="mt-1 text-slate-600">
                      批准后余额{' '}
                      <span
                        className={cn(
                          'font-semibold',
                          (after ?? 0) < 0 ? 'text-rose-600' : 'text-emerald-600',
                        )}
                      >
                        {after}
                      </span>{' '}
                      分
                    </p>
                    {(after ?? 0) < 0 && (
                      <p className="mt-1 text-xs text-rose-600">
                        批准后余额会变成负数（透支）。孩子会看到「积分透支，下次打卡先补回来」的提示。
                      </p>
                    )}
                  </div>
                );
              })()
            )}

            <Input
              label={decision.decision === 'reject' ? '拒绝原因（可选，会显示给孩子）' : '备注（可选）'}
              hint={
                decision.decision === 'approve' || decision.decision === 'fulfill'
                  ? '孩子会看到这句话。'
                  : undefined
              }
              value={note}
              maxLength={200}
              onChange={(e) => setNote(e.target.value)}
            />

            {error && (
              <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </p>
            )}
          </div>
        )}
      </Modal>
    </AppShell>
  );
}
